import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Check, ChevronRight, FlaskConical } from "lucide-react";
import { Flame } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { SPORT_META } from "@/lib/odds";
import { useAuth } from "@/context/AuthContext";
import { FollowBettorButton } from "@/components/FollowBettorButton";

const STEPS = ["Your sports", "Your teams", "Bankroll", "Your crew"];
const LAST = 3;

export function Onboarding() {
  const { user, setUser } = useAuth();
  const { pathname } = useLocation();
  const [step, setStep] = useState(0);
  const [sports, setSports] = useState([]);
  const [amount, setAmount] = useState(500);
  const [method, setMethod] = useState("kelly");
  const [bettors, setBettors] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [teams, setTeams] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user && !user.onboarded && step === LAST) api.get("/bettors").then((r) => setBettors(r.data.bettors.slice(0, 5))).catch(() => {}); }, [step, user]);
  useEffect(() => { if (user && !user.onboarded && step === 1) api.get(`/teams/suggest?sports=${sports.join(",")}`).then((r) => setSuggested(r.data.teams)).catch(() => {}); }, [step, user, sports]);
  if (!user || user.onboarded || !["/dashboard", "/props", "/players", "/leaderboard", "/builder"].includes(pathname)) return null;

  const finish = async (skip = false) => {
    setBusy(true);
    try {
      if (!skip) await api.patch("/users/me/bankroll", { amount: Number(amount) || 0, method, unit_pct: 1 });
      if (teams.length) await api.post("/teams/watch/set", { teams });
      const { data } = await api.post("/users/me/onboard", { sports, onboarded: true });
      setUser((u) => ({ ...u, ...data, teams, onboarded: true }));
      toast.success(skip ? "You can set this up anytime from Performance." : "You're set — welcome to the Lab");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const next = async () => {
    if (step === 1 && teams.length) {
      try { const { data } = await api.post("/teams/watch/set", { teams }); setUser((u) => ({ ...u, teams: data.teams })); } catch (e) { toast.error(errMsg(e)); }
    }
    if (step === 2) {
      try { await api.patch("/users/me/bankroll", { amount: Number(amount) || 0, method, unit_pct: 1 }); } catch (e) { toast.error(errMsg(e)); }
    }
    step < LAST ? setStep(step + 1) : finish();
  };
  const toggleTeam = (k) => setTeams((t) => (t.includes(k) ? t.filter((x) => x !== k) : [...t, k]));

  return (
    <div data-testid="onboarding-modal" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
      <div className="glass-card fade-up w-full max-w-lg !bg-[#0B0E14] p-6 shadow-2xl">
        <div className="flex items-center gap-2 text-emerald-400"><FlaskConical size={18} /><span className="font-display text-xl font-black uppercase tracking-tight text-white">Welcome to the Lab, {user.name.split(" ")[0]}</span></div>
        <ol className="mt-4 flex gap-2" data-testid="onboarding-steps">{STEPS.map((s, i) => <li key={s} className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider ${i === step ? "text-emerald-400" : i < step ? "text-slate-300" : "text-slate-600"}`}>{i < step ? <Check size={11} /> : <span className="font-mono-num">{i + 1}</span>} {s}</li>)}</ol>
        <div className="mt-5 min-h-[190px]">
          {step === 0 && (
            <>
              <p className="text-sm text-slate-300">Which sports do you bet? We'll surface those first.</p>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="onboarding-sports">
                {Object.entries(SPORT_META).map(([k, m]) => { const on = sports.includes(k); return <button key={k} data-testid={`onboarding-sport-${k}`} onClick={() => setSports((s) => (on ? s.filter((x) => x !== k) : [...s, k]))} className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${on ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-[#26334D] text-slate-400 hover:text-white"}`} style={on ? {} : { borderColor: undefined }}>{m.label}</button>; })}
              </div>
            </>
          )}
          {step === 1 && (
            <>
              <p className="text-sm text-slate-300">Follow three teams — their games, the model's calls and standings moves lead your dashboard, with alerts when they move. <span className="font-mono-num text-emerald-400" data-testid="onboarding-teams-count">{teams.length}/3</span></p>
              <div className="mt-3 flex max-h-56 flex-wrap gap-2 overflow-y-auto" data-testid="onboarding-teams">
                {suggested.map((t) => { const on = teams.includes(t.key); return <button key={t.key} data-testid={`onboarding-team-${t.slug}`} onClick={() => toggleTeam(t.key)} className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "border-amber-400 bg-amber-500/15 text-amber-200" : "border-[#26334D] text-slate-300 hover:text-white"}`}><span className="h-1.5 w-1.5 rounded-full" style={{ background: SPORT_META[t.sport]?.color }} />{t.team}<span className="font-mono-num text-[10px] text-slate-500">#{t.rank} · {t.streak}</span>{t.heat === "hot" && <Flame size={10} className="text-orange-300" />}</button>; })}
                {!suggested.length && <p className="text-xs text-slate-500">Loading league leaders and hot teams…</p>}
              </div>
              <p className="mt-2 text-[11px] text-slate-500">Showing league leaders and teams on a heater from your sports. You can follow any team later from Standings.</p>
            </>
          )}
          {step === 2 && (
            <>
              <p className="text-sm text-slate-300">Set a bankroll and we'll size every ticket for you.</p>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <label className="text-[10px] uppercase tracking-wider text-slate-500">Bankroll $<input data-testid="onboarding-bankroll" type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="input-dark mt-1 block w-36" /></label>
                <div className="flex gap-2">{[["kelly", "¼ Kelly"], ["flat", "Flat 1u"]].map(([k, l]) => <button key={k} data-testid={`onboarding-method-${k}`} onClick={() => setMethod(k)} className={`rounded-full border px-3 py-1.5 text-xs font-bold ${method === k ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-[#26334D] text-slate-400"}`}>{l}</button>)}</div>
              </div>
            </>
          )}
          {step === LAST && (
            <>
              <p className="text-sm text-slate-300">Follow a few sharp bettors — their live tickets show up on your crew board.</p>
              <ul className="mt-3 space-y-1.5" data-testid="onboarding-bettors">{bettors.map((b) => <li key={b.user_id} className="flex items-center gap-3 rounded-lg bg-[#121824] px-3 py-2 text-sm"><span className="min-w-0 flex-1 truncate font-semibold text-white">{b.name} <span className="font-mono-num text-xs text-slate-500">{b.won}–{b.lost} · ROI {b.roi >= 0 ? "+" : ""}{b.roi}%</span></span><FollowBettorButton userId={b.user_id} name={b.name} /></li>)}</ul>
            </>
          )}
        </div>
        <div className="mt-5 flex items-center gap-3">
          <button data-testid="onboarding-skip" onClick={() => finish(true)} disabled={busy} className="text-xs text-slate-500 hover:text-white">Skip for now</button>
          <button data-testid="onboarding-next" onClick={next} disabled={busy} className="btn-primary ml-auto !py-2 text-sm">{step < LAST ? <>Next <ChevronRight size={14} /></> : "Enter the Lab"}</button>
        </div>
      </div>
    </div>
  );
}
